import type { CabinetContext } from "@planerius/contracts/cabinet-auth"
import { useLocation, useNavigate } from "@tanstack/react-router"
import { useCallback } from "react"

import { CABINET_PATHS } from "./nav-items"
import { rememberBranch } from "./use-branch"

/**
 * Смена филиала из шапки.
 *
 * ВЫБОР ЖИВЁТ В ДВУХ МЕСТАХ, И ОБА ОБЯЗАТЕЛЬНЫ.
 *
 * 1. В АДРЕСЕ — там, где раздел его принимает. Ссылка на день конкретного филиала,
 *    присланная в чате, обязана открыть именно его; без параметра в адресе она откроет
 *    тот филиал, где получатель был вчера, и это тихая ошибка, которую никто не заметит.
 * 2. В ПАМЯТИ УСТРОЙСТВА — всегда. Разделы без параметра в адресе (настройки, тариф)
 *    иначе забывали бы выбор при каждом переходе, и человек переключал бы точку заново
 *    по десять раз в день. Память своя на каждом устройстве: у администратора за стойкой
 *    и у него же в телефоне филиалы разные (ТЗ §4.4).
 *
 * Сегодня параметр `branch` принимает только журнал, и здесь это написано прямо, одной
 * веткой. Список «разделов с филиалом в адресе» тут был бы ловушкой: он выглядит
 * расширяемым, а переход всё равно один — второй раздел пришлось бы дописывать
 * не в список, а сюда же, и расхождение заметили бы не сразу.
 */
function isJournal(pathname: string): boolean {
  const journal: string = CABINET_PATHS.journal
  return pathname === journal || pathname.startsWith(`${journal}/`)
}

export function useBranchPicker(context: CabinetContext): (branchId: string) => void {
  const navigate = useNavigate()
  const pathname = useLocation({ select: (location) => location.pathname })

  return useCallback(
    (branchId: string) => {
      rememberBranch(context, branchId)
      if (!isJournal(pathname)) return
      void navigate({
        to: CABINET_PATHS.journal,
        search: (previous) => ({ ...previous, branch: branchId }),
      })
    },
    [context, navigate, pathname],
  )
}
